import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { autoAcceptRulesService } from '../../services/autoAcceptRulesService';
import { deliveryPolygonsService } from '../../services/deliveryPolygonsService';
import { AutoAcceptRule, CreateAutoAcceptRuleRequest, UpdateAutoAcceptRuleRequest } from '../../types/autoAcceptRules';
import { DeliveryPolygon } from '../../types/deliveryPolygons';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './Page.css';

interface RuleFormData {
  name: string;
  priority: number;
  delayTimeSeconds: number;
  minOrderPrice: string;
  maxOrderPrice: string;
  minClientSuccessfulOrders: string;
  clientLocationPolygonId: string;
  isActive: boolean;
}

interface SortableRuleItemProps {
  rule: AutoAcceptRule;
  onEdit: (rule: AutoAcceptRule) => void;
  onDelete: (rule: AutoAcceptRule) => void;
  onToggleActive: (rule: AutoAcceptRule) => void;
}

const SortableRuleItem: React.FC<SortableRuleItemProps> = ({ rule, onEdit, onDelete, onToggleActive }) => {
  const { t } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: rule.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return null;
    return price.toLocaleString('en-US');
  };

  const formatDelayTime = (seconds: number) => {
    if (seconds < 60) return `${seconds} ${t('autoAcceptRules.seconds')}`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes} ${t('autoAcceptRules.minutes')}`;
    return `${minutes} ${t('autoAcceptRules.minutes')} ${remainingSeconds} ${t('autoAcceptRules.seconds')}`;
  };

  const getPriorityBadgeColor = (priority: number) => {
    if (priority === 1) return '#dc3545';
    if (priority <= 3) return '#fd7e14';
    return '#28a745';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="detail-card"
    >
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
        {/* Drag Handle */}
        <div
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            padding: '0.5rem',
            fontSize: '1.5rem',
            color: '#666',
            userSelect: 'none',
          }}
        >
          ☰
        </div>

        {/* Rule Content */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <span
              style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                backgroundColor: getPriorityBadgeColor(rule.priority),
                color: 'white',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              {t('autoAcceptRules.priority')}: {rule.priority}
            </span>
            <h3 style={{ margin: 0, flex: 1 }}>{rule.name}</h3>
            <span
              style={{
                display: 'inline-block',
                padding: '0.25rem 0.75rem',
                borderRadius: '12px',
                backgroundColor: rule.isActive ? '#28a745' : '#6c757d',
                color: 'white',
                fontSize: '0.85rem',
                fontWeight: 'bold',
              }}
            >
              {rule.isActive ? t('autoAcceptRules.active') : t('autoAcceptRules.inactive')}
            </span>
          </div>

          {/* Conditions */}
          <div style={{ marginBottom: '0.75rem', fontSize: '0.95rem', color: '#333' }}>
            {(rule.minOrderPrice !== null || rule.maxOrderPrice !== null) && (
              <div style={{ marginBottom: '0.25rem' }}>
                <span style={{ marginRight: '0.5rem' }}>💰</span>
                {t('autoAcceptRules.price')}:{' '}
                {rule.minOrderPrice !== null && rule.maxOrderPrice !== null
                  ? `${formatPrice(rule.minOrderPrice)} - ${formatPrice(rule.maxOrderPrice)} UZS`
                  : rule.minOrderPrice !== null
                  ? `${t('autoAcceptRules.from')} ${formatPrice(rule.minOrderPrice)} UZS`
                  : `${t('autoAcceptRules.upTo')} ${formatPrice(rule.maxOrderPrice)} UZS`}
              </div>
            )}
            {rule.minClientSuccessfulOrders !== null && (
              <div style={{ marginBottom: '0.25rem' }}>
                <span style={{ marginRight: '0.5rem' }}>📊</span>
                {t('autoAcceptRules.minOrders')}: {rule.minClientSuccessfulOrders}
              </div>
            )}
            {rule.clientLocationPolygonTitle !== null && (
              <div style={{ marginBottom: '0.25rem' }}>
                <span style={{ marginRight: '0.5rem' }}>📍</span>
                {t('autoAcceptRules.location')}: {rule.clientLocationPolygonTitle}
              </div>
            )}
            <div>
              <span style={{ marginRight: '0.5rem' }}>⏱️</span>
              {t('autoAcceptRules.delay')}: {formatDelayTime(rule.delayTimeSeconds)}
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => onEdit(rule)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {t('autoAcceptRules.edit')}
            </button>
            <button
              onClick={() => onToggleActive(rule)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: rule.isActive ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {rule.isActive ? t('autoAcceptRules.deactivate') : t('autoAcceptRules.activate')}
            </button>
            <button
              onClick={() => onDelete(rule)}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {t('autoAcceptRules.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AutoAcceptRules: React.FC = () => {
  const { t } = useTranslation();
  const [rules, setRules] = useState<AutoAcceptRule[]>([]);
  const [polygons, setPolygons] = useState<DeliveryPolygon[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AutoAcceptRule | null>(null);
  const [deleteConfirmRule, setDeleteConfirmRule] = useState<AutoAcceptRule | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Form state
  const [formData, setFormData] = useState<RuleFormData>({
    name: '',
    priority: 1,
    delayTimeSeconds: 30,
    minOrderPrice: '',
    maxOrderPrice: '',
    minClientSuccessfulOrders: '',
    clientLocationPolygonId: '',
    isActive: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchRules = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await autoAcceptRulesService.getAllRules();
      if (response.success && response.data) {
        setRules(response.data.sort((a, b) => a.priority - b.priority));
      } else {
        setError(response.error || 'Failed to fetch rules');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPolygons = async () => {
    try {
      const response = await deliveryPolygonsService.getAllPolygons();
      if (response.success && response.data) {
        setPolygons(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch polygons:', error);
    }
  };

  useEffect(() => {
    fetchRules();
    fetchPolygons();
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = rules.findIndex((r) => r.id === active.id);
      const newIndex = rules.findIndex((r) => r.id === over.id);

      const newRules = arrayMove(rules, oldIndex, newIndex);

      // Update priorities based on new order
      const updatedRules = newRules.map((rule, index) => ({
        ...rule,
        priority: index + 1,
      }));

      setRules(updatedRules);

      // Send bulk update to backend
      try {
        const prioritiesRequest = {
          rules: updatedRules.map((rule) => ({
            id: rule.id,
            priority: rule.priority,
          })),
        };

        const response = await autoAcceptRulesService.bulkUpdatePriorities(prioritiesRequest);
        if (response.success) {
          setSuccessMessage(t('autoAcceptRules.prioritiesUpdated'));
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to update priorities');
          // Revert on error
          fetchRules();
        }
      } catch (error) {
        setError('Failed to update priorities');
        fetchRules();
      }
    }
  };

  const handleOpenModal = (rule?: AutoAcceptRule) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        name: rule.name,
        priority: rule.priority,
        delayTimeSeconds: rule.delayTimeSeconds,
        minOrderPrice: rule.minOrderPrice !== null ? rule.minOrderPrice.toString() : '',
        maxOrderPrice: rule.maxOrderPrice !== null ? rule.maxOrderPrice.toString() : '',
        minClientSuccessfulOrders: rule.minClientSuccessfulOrders !== null ? rule.minClientSuccessfulOrders.toString() : '',
        clientLocationPolygonId: rule.clientLocationPolygonId !== null ? rule.clientLocationPolygonId.toString() : '',
        isActive: rule.isActive,
      });
    } else {
      setEditingRule(null);
      const nextPriority = rules.length > 0 ? Math.max(...rules.map(r => r.priority)) + 1 : 1;
      setFormData({
        name: '',
        priority: nextPriority,
        delayTimeSeconds: 30,
        minOrderPrice: '',
        maxOrderPrice: '',
        minClientSuccessfulOrders: '',
        clientLocationPolygonId: '',
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingRule(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError(t('autoAcceptRules.nameRequired'));
      return;
    }

    if (formData.priority < 1) {
      setError(t('autoAcceptRules.priorityPositive'));
      return;
    }

    if (formData.delayTimeSeconds < 1) {
      setError(t('autoAcceptRules.delayPositive'));
      return;
    }

    const minPrice = formData.minOrderPrice ? parseInt(formData.minOrderPrice) : null;
    const maxPrice = formData.maxOrderPrice ? parseInt(formData.maxOrderPrice) : null;

    if (minPrice !== null && maxPrice !== null && minPrice >= maxPrice) {
      setError(t('autoAcceptRules.priceRangeInvalid'));
      return;
    }

    try {
      if (editingRule) {
        // Update existing rule
        const request: UpdateAutoAcceptRuleRequest = {
          id: editingRule.id,
          name: formData.name.trim(),
          priority: formData.priority,
          delayTimeSeconds: formData.delayTimeSeconds,
          minOrderPrice: minPrice,
          maxOrderPrice: maxPrice,
          minClientSuccessfulOrders: formData.minClientSuccessfulOrders ? parseInt(formData.minClientSuccessfulOrders) : null,
          clientLocationPolygonId: formData.clientLocationPolygonId ? parseInt(formData.clientLocationPolygonId) : null,
          isActive: formData.isActive,
        };

        const response = await autoAcceptRulesService.updateRule(request);
        if (response.success) {
          setSuccessMessage(t('autoAcceptRules.ruleUpdated'));
          handleCloseModal();
          fetchRules();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to update rule');
        }
      } else {
        // Create new rule
        const request: CreateAutoAcceptRuleRequest = {
          name: formData.name.trim(),
          priority: formData.priority,
          delayTimeSeconds: formData.delayTimeSeconds,
          minOrderPrice: minPrice,
          maxOrderPrice: maxPrice,
          minClientSuccessfulOrders: formData.minClientSuccessfulOrders ? parseInt(formData.minClientSuccessfulOrders) : null,
          clientLocationPolygonId: formData.clientLocationPolygonId ? parseInt(formData.clientLocationPolygonId) : null,
          isActive: formData.isActive,
        };

        const response = await autoAcceptRulesService.createRule(request);
        if (response.success) {
          setSuccessMessage(t('autoAcceptRules.ruleCreated'));
          handleCloseModal();
          fetchRules();
          setTimeout(() => setSuccessMessage(''), 3000);
        } else {
          setError(response.error || 'Failed to create rule');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleToggleActive = async (rule: AutoAcceptRule) => {
    try {
      const request: UpdateAutoAcceptRuleRequest = {
        id: rule.id,
        name: rule.name,
        priority: rule.priority,
        delayTimeSeconds: rule.delayTimeSeconds,
        minOrderPrice: rule.minOrderPrice,
        maxOrderPrice: rule.maxOrderPrice,
        minClientSuccessfulOrders: rule.minClientSuccessfulOrders,
        clientLocationPolygonId: rule.clientLocationPolygonId,
        isActive: !rule.isActive,
      };

      const response = await autoAcceptRulesService.updateRule(request);
      if (response.success) {
        setSuccessMessage(rule.isActive ? t('autoAcceptRules.ruleDeactivated') : t('autoAcceptRules.ruleActivated'));
        fetchRules();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to toggle rule');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleDelete = async (rule: AutoAcceptRule) => {
    setDeleteConfirmRule(rule);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmRule) return;

    try {
      const response = await autoAcceptRulesService.deleteRule(deleteConfirmRule.id);
      if (response.success) {
        setSuccessMessage(t('autoAcceptRules.ruleDeleted'));
        setDeleteConfirmRule(null);
        fetchRules();
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to delete rule');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const filteredRules = rules.filter((rule) =>
    rule.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1>{t('autoAcceptRules.title')}</h1>
        <p>{t('autoAcceptRules.subtitle')}</p>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '1rem',
              backgroundColor: '#d4edda',
              color: '#155724',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              marginBottom: '1.5rem',
            }}
          >
            {successMessage}
          </div>
        )}

        {/* Search and Create */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('autoAcceptRules.searchPlaceholder')}
            className="search-input"
            style={{ flex: 1, minWidth: '200px' }}
          />
          <button
            onClick={() => handleOpenModal()}
            className="search-button"
            style={{ whiteSpace: 'nowrap' }}
          >
            {t('autoAcceptRules.createRule')}
          </button>
        </div>

        {/* Rules List */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {t('autoAcceptRules.loading')}
          </div>
        ) : filteredRules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            {searchQuery ? t('autoAcceptRules.noRulesFound') : t('autoAcceptRules.noRules')}
          </div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredRules.map(r => r.id)} strategy={verticalListSortingStrategy}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {filteredRules.map((rule) => (
                  <SortableRuleItem
                    key={rule.id}
                    rule={rule}
                    onEdit={handleOpenModal}
                    onDelete={handleDelete}
                    onToggleActive={handleToggleActive}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={handleCloseModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingRule ? t('autoAcceptRules.editRule') : t('autoAcceptRules.createNewRule')}
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Rule Name */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('autoAcceptRules.ruleName')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="search-input"
                  placeholder={t('autoAcceptRules.ruleNamePlaceholder')}
                  required
                />
              </div>

              {/* Priority and Delay */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    {t('autoAcceptRules.priority')} *
                  </label>
                  <input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 1 })}
                    className="search-input"
                    min="1"
                    required
                  />
                  <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                    {t('autoAcceptRules.priorityHint')}
                  </small>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    {t('autoAcceptRules.delaySeconds')} *
                  </label>
                  <input
                    type="number"
                    value={formData.delayTimeSeconds}
                    onChange={(e) => setFormData({ ...formData, delayTimeSeconds: parseInt(e.target.value) || 1 })}
                    className="search-input"
                    min="1"
                    required
                  />
                </div>
              </div>

              {/* Price Range */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('autoAcceptRules.priceRange')}
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <input
                      type="number"
                      value={formData.minOrderPrice}
                      onChange={(e) => setFormData({ ...formData, minOrderPrice: e.target.value })}
                      className="search-input"
                      placeholder={t('autoAcceptRules.minPrice')}
                      step="1"
                      min="0"
                    />
                  </div>
                  <div>
                    <input
                      type="number"
                      value={formData.maxOrderPrice}
                      onChange={(e) => setFormData({ ...formData, maxOrderPrice: e.target.value })}
                      className="search-input"
                      placeholder={t('autoAcceptRules.maxPrice')}
                      step="1"
                      min="0"
                    />
                  </div>
                </div>
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                  {t('autoAcceptRules.priceRangeHint')}
                </small>
              </div>

              {/* Min Client Orders */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('autoAcceptRules.minClientOrders')}
                </label>
                <input
                  type="number"
                  value={formData.minClientSuccessfulOrders}
                  onChange={(e) => setFormData({ ...formData, minClientSuccessfulOrders: e.target.value })}
                  className="search-input"
                  placeholder={t('autoAcceptRules.minClientOrdersPlaceholder')}
                  min="0"
                />
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                  {t('autoAcceptRules.minClientOrdersHint')}
                </small>
              </div>

              {/* Location Polygon */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                  {t('autoAcceptRules.locationPolygon')}
                </label>
                <select
                  value={formData.clientLocationPolygonId}
                  onChange={(e) => setFormData({ ...formData, clientLocationPolygonId: e.target.value })}
                  className="search-input"
                  style={{ width: '100%' }}
                >
                  <option value="">{t('autoAcceptRules.anyLocation')}</option>
                  {polygons.map((polygon) => (
                    <option key={polygon.id} value={polygon.id}>
                      {polygon.title}
                    </option>
                  ))}
                </select>
                <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>
                  {t('autoAcceptRules.locationPolygonHint')}
                </small>
              </div>

              {/* Active Status */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    style={{ width: '20px', height: '20px', marginRight: '0.75rem' }}
                  />
                  <span style={{ fontWeight: 500 }}>{t('autoAcceptRules.activeRule')}</span>
                </label>
              </div>

              {/* Buttons */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('autoAcceptRules.cancel')}
                </button>
                <button
                  type="submit"
                  className="search-button"
                  style={{ padding: '0.75rem 1.5rem' }}
                >
                  {editingRule ? t('autoAcceptRules.saveChanges') : t('autoAcceptRules.createRule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmRule && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setDeleteConfirmRule(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '2rem',
              maxWidth: '500px',
              width: '100%',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0, color: '#dc3545' }}>{t('autoAcceptRules.confirmDelete')}</h2>
            <p>{t('autoAcceptRules.confirmDeleteMessage', { name: deleteConfirmRule.name })}</p>
            {deleteConfirmRule.isActive && (
              <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
                {t('autoAcceptRules.warningActiveRule')}
              </p>
            )}
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button
                onClick={() => setDeleteConfirmRule(null)}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t('autoAcceptRules.cancel')}
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                {t('autoAcceptRules.confirmDeleteButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoAcceptRules;
